// ── 월별 리포트 (통계 탭) ─────────────────────────────────────
// 계산은 reportEngine.js, 이 파일은 화면만 담당.
// 차트는 외부 라이브러리 없이 SVG로 직접 그림 (설치 불필요, 번들 영향 없음).
//
// 디자인 기준: Electron 데스크톱 버전과 동일한 톤.
//  - 굵기는 제목·금액만 700, 나머지 400~500
//  - 글자 크기: 보조 12 / 본문 13~14 / 핵심 금액 18~20
//  - 테두리 1px, 모서리 14, 배지·강조는 반투명 톤으로 은은하게

import React, { useMemo, useState } from "react";
import { INCOME_CATS, EXPENSE_CATS, buildMonthlyCards, monthLabel } from "./reportEngine";
import CharAvatar from "./CharAvatar";

export const ACCENT = "#5B4FD0";              // 테마 작업(2단계) 때 테마 토큰으로 교체 예정
const ACCENT_SOFT = "rgba(91,79,208,0.16)";
export const UP = "#20C07A";
export const DOWN = "#F0475A";
const PATCH = "#E8A33D";
const PATCH_SOFT = "rgba(232,163,61,0.13)";

function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
// 차트·범례용 짧은 금액 (예: 12.3억, 850만)
export function short(v) {
  const a = Math.abs(v), s = v < 0 ? "-" : "";
  if (a >= 1e8) return `${s}${(a / 1e8).toFixed(a >= 1e10 ? 0 : 1)}억`;
  if (a >= 1e4) return `${s}${Math.round(a / 1e4).toLocaleString()}만`;
  return `${s}${Math.round(a).toLocaleString()}`;
}

const cardStyle = (T, pad = 20) => ({ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: pad });
const titleStyle = T => ({ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 });

function Pct({ value, T }) {
  if (value == null) return <span style={{ fontSize: 12, color: T.sub }}>비교 기록 없음</span>;
  const up = value >= 0;
  return <span style={{ fontSize: 12, fontWeight: 600, color: up ? UP : DOWN }}>{up ? "▲" : "▼"} {Math.abs(value).toFixed(1)}%</span>;
}

// ── 도넛(원) 그래프 ──
export function Donut({ items, T, size = 132, thickness = 16, label, value }) {
  const total = items.reduce((s, i) => s + Math.max(0, i.value), 0);
  const r = (size - thickness) / 2, C = 2 * Math.PI * r, c = size / 2;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={c} cy={c} r={r} fill="none" stroke={T.border} strokeWidth={thickness} />
      {total > 0 && items.filter(i => i.value > 0).map(i => {
        const len = (i.value / total) * C;
        const el = <circle key={i.key} cx={c} cy={c} r={r} fill="none" stroke={i.color} strokeWidth={thickness}
          strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-acc} transform={`rotate(-90 ${c} ${c})`} />;
        acc += len;
        return el;
      })}
      <text x={c} y={c - 4} textAnchor="middle" fontSize="11" fill={T.sub}>{label}</text>
      <text x={c} y={c + 14} textAnchor="middle" fontSize="14" fontWeight="700" fill={T.text}>{value}</text>
    </svg>
  );
}

export function Legend({ items, total, T, prevMap }) {
  const shown = items.filter(i => i.value > 0);
  if (!shown.length) return <div style={{ fontSize: 13, color: T.sub }}>기록 없음</div>;
  return (
    <div style={{ flex: 1, minWidth: 200, maxWidth: 380, display: "flex", flexDirection: "column", gap: 9 }}>
      {shown.map(i => {
        const pv = prevMap ? prevMap[i.key] || 0 : 0;
        const d = prevMap && pv > 0 ? ((i.value - pv) / pv) * 100 : null;
        return (
          <div key={i.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: i.color, flexShrink: 0 }} />
            <span style={{ color: T.text, flex: 1, whiteSpace: "nowrap" }}>{i.label}</span>
            <span style={{ color: T.sub, width: 34, textAlign: "right" }}>{Math.round((i.value / total) * 100)}%</span>
            <span style={{ color: T.text, fontWeight: 600, width: 62, textAlign: "right" }}>{short(i.value)}</span>
            <span style={{ width: 46, textAlign: "right", fontSize: 12, fontWeight: 600, color: d == null ? T.sub : d >= 0 ? UP : DOWN }}>
              {d == null ? "" : `${d >= 0 ? "▲" : "▼"}${Math.abs(d).toFixed(0)}%`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── 최근 6개월 추이 (수익·지출 막대) ──
function TrendChart({ cards, T, isMobile }) {
  const months = cards.slice(0, 6).reverse();
  // 모바일은 좁은 좌표계를 써서 글자가 작아지지 않게 함 (viewBox가 화면 폭에 맞춰 축소되기 때문)
  const W = isMobile ? 340 : 640, H = isMobile ? 170 : 200, padB = 28, padT = 22;
  const maxV = Math.max(1, ...months.flatMap(m => [m.summary.farmIncome, m.summary.totalExpense]));
  const slot = W / months.length, bw = Math.min(22, slot / 4);
  const y = v => padT + (H - padT - padB) * (1 - v / maxV);
  const base = H - padB;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      {[0.5, 1].map(f => <line key={f} x1="0" x2={W} y1={y(maxV * f)} y2={y(maxV * f)} stroke={T.border} strokeDasharray="3 4" />)}
      <line x1="0" x2={W} y1={base} y2={base} stroke={T.border} />
      {months.map((m, i) => {
        const cx = slot * i + slot / 2, inc = m.summary.farmIncome, exp = m.summary.totalExpense;
        const op = m.isCurrent ? 0.5 : 1;
        return (
          <g key={m.key}>
            <rect x={cx - bw - 2} y={y(inc)} width={bw} height={base - y(inc)} rx="3" fill={ACCENT} opacity={op} />
            <rect x={cx + 2} y={y(exp)} width={bw} height={base - y(exp)} rx="3" fill={DOWN} opacity={op * 0.75} />
            <text x={cx - bw / 2 - 2} y={y(inc) - 6} textAnchor="middle" fontSize="11" fontWeight="600" fill={T.sub}>{short(inc)}</text>
            <text x={cx} y={H - 8} textAnchor="middle" fontSize="12" fill={m.isCurrent ? T.text : T.sub}>
              {monthLabel(m.key, false)}{m.isCurrent && !isMobile ? "·진행중" : ""}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ── 월 한 줄 (펼치면 상세) ──
function MonthRow({ card, T, fmt, open, onToggle, last }) {
  const s = card.summary;
  const inc = INCOME_CATS.map(c => ({ ...c, value: s.income[c.key] }));
  const exp = EXPENSE_CATS.map(c => ({ ...c, value: s.expense[c.key] }));
  return (
    <div style={{ borderBottom: last ? "none" : `1px solid ${T.border}` }}>
      <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 2px", cursor: "pointer" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {monthLabel(card.key)}
            {card.isCurrent && <span style={{ fontSize: 11, fontWeight: 600, color: ACCENT, background: ACCENT_SOFT, padding: "2px 7px", borderRadius: 6 }}>진행 중</span>}
            {card.patches.length > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: PATCH, background: PATCH_SOFT, padding: "2px 7px", borderRadius: 6 }}>⚡ 패치</span>}
          </div>
          <div style={{ fontSize: 12, color: T.sub, marginTop: 3 }}>
            기록 {s.recordDays}일{s.topIncome ? ` · 주 수익원 ${s.topIncome.label} ${Math.round(s.topIncome.share * 100)}%` : ""}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{fmt(s.farmIncome)}</div>
          <div style={{ marginTop: 2 }}><Pct value={card.incomePct} T={T} /></div>
        </div>
        <span style={{ fontSize: 11, color: T.sub, width: 12, textAlign: "center" }}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div style={{ padding: "4px 2px 18px", display: "flex", flexDirection: "column", gap: 18 }}>
          {card.patches.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {card.patches.map(p => <div key={p.label} style={{ fontSize: 12, color: PATCH }}>⚡ {p.label}</div>)}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            {[["파밍 수익", s.farmIncome, UP], ["지출", s.totalExpense, DOWN], ["순이익", s.net, s.net >= 0 ? ACCENT : DOWN]].map(([l, v, col]) => (
              <div key={l} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 12, color: T.sub }}>{l}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: col, marginTop: 3 }}>{short(v)}</div>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>
              수익 구성 {card.prev && <span style={{ fontSize: 12, fontWeight: 400, color: T.sub }}>· 오른쪽 %는 {card.compareLabel}</span>}
            </div>
            <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              <Donut items={inc} T={T} label="파밍 수익" value={short(s.farmIncome)} />
              <Legend items={inc} total={s.farmIncome} T={T} prevMap={card.prev?.income} />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>지출 구성</div>
            <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              <Donut items={exp} T={T} label="지출" value={short(s.totalExpense)} />
              <Legend items={exp} total={s.totalExpense} T={T} prevMap={card.prev?.expense} />
            </div>
          </div>
          {(s.exchangeBuy > 0 || s.exchangeSell > 0) && (
            <div style={{ fontSize: 12, color: T.sub, lineHeight: 1.6 }}>
              💱 환전은 재화 교환이라 위 수익·지출에서 제외했어요.
              {s.exchangeBuy > 0 && ` 메소 구매 ${fmt(s.exchangeBuy)}`}
              {s.exchangeSell > 0 && ` · 메소 판매 ${fmt(s.exchangeSell)}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 월별 리포트 본체 ──
export function MonthlyReport({ records, favorites, T, isMobile, selChar, fmt, calcIncome, calcSolIncome, defaultOpenKey }) {
  const [charKey, setCharKey] = useState(selChar?.nickname || "__all__");
  const [includeSol, setIncludeSol] = useState(true);
  const [openKey, setOpenKey] = useState(defaultOpenKey || null);

  const nicknames = useMemo(() => {
    const fav = favorites.map(f => f.nickname);
    return [...fav, ...new Set(records.map(r => r.nickname).filter(n => n && !fav.includes(n)))];
  }, [records, favorites]);

  const todayStr = localToday();
  const cards = useMemo(() => {
    const recs = charKey === "__all__" ? records : records.filter(r => r.nickname === charKey);
    return buildMonthlyCards(recs, { favorites, includeSol, calcIncome, calcSolIncome }, todayStr);
  }, [records, favorites, charKey, includeSol, calcIncome, calcSolIncome, todayStr]);

  const cur = cards[0];
  const selFav = favorites.find(f => f.nickname === charKey);
  const pill = (key, label, image) => {
    const on = charKey === key;
    return (
      <button key={key} onClick={() => { setCharKey(key); setOpenKey(null); }}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: image ? "3px 12px 3px 3px" : "7px 12px", borderRadius: 20,
          fontSize: 13, fontWeight: on ? 600 : 400, cursor: "pointer",
          border: `1px solid ${on ? ACCENT : T.border}`, background: on ? ACCENT_SOFT : "transparent", color: on ? T.text : T.sub }}>
        {image && <CharAvatar src={image} size={26} />}
        {label}
      </button>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* 캐릭터 + 조각 스위치 */}
      <div style={cardStyle(T, isMobile ? 16 : 20)}>
        <div style={titleStyle(T)}>👤 캐릭터</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {pill("__all__", "전체")}
          {nicknames.map(n => pill(n, n, favorites.find(f => f.nickname === n)?.image))}
        </div>
        <div onClick={() => setIncludeSol(v => !v)}
          style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}`, cursor: "pointer" }}>
          <span style={{ width: 34, height: 20, borderRadius: 10, background: includeSol ? ACCENT : T.border, position: "relative", flexShrink: 0, transition: "background .15s" }}>
            <span style={{ position: "absolute", top: 3, left: includeSol ? 17 : 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
          </span>
          <div>
            <div style={{ fontSize: 13, color: T.text }}>조각(솔 에르다) 가치 포함</div>
            <div style={{ fontSize: 12, color: T.sub, marginTop: 1 }}>
              {includeSol ? "조각을 직접 쓰는 분께 맞는 계산이에요" : "조각을 경매장에 파는 분께 맞는 계산이에요 (판매액은 경매장에 포함)"}
            </div>
          </div>
        </div>
      </div>

      {!cur ? (
        <div style={{ ...cardStyle(T, 40), textAlign: "center", color: T.sub, fontSize: 13 }}>
          아직 기록이 없어요. 수익이나 지출을 기록하면 월별 리포트가 만들어져요.
        </div>
      ) : (
        <>
          {/* 선택한 캐릭터 */}
          {selFav && (
            <div style={{ ...cardStyle(T, 16), display: "flex", alignItems: "center", gap: 14 }}>
              <CharAvatar src={selFav.image} size={64} radius={14} bg={T.bg} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{selFav.nickname}</div>
                <div style={{ fontSize: 12, color: T.sub, marginTop: 3 }}>
                  {[selFav.job, selFav.world, selFav.level && `Lv.${selFav.level}`].filter(Boolean).join(" · ")}
                </div>
              </div>
            </div>
          )}

          {/* 이번 달 요약 타일 */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: 12 }}>
            {[
              [`${monthLabel(cur.key, false)} 파밍 수익`, cur.summary.farmIncome, UP, true],
              [`${monthLabel(cur.key, false)} 지출`, cur.summary.totalExpense, DOWN, false],
              [`${monthLabel(cur.key, false)} 순이익`, cur.summary.net, cur.summary.net >= 0 ? ACCENT : DOWN, false],
            ].map(([l, v, col, main]) => (
              <div key={l} style={{ ...cardStyle(T, 16), gridColumn: isMobile && main ? "1 / -1" : "auto" }}>
                <div style={{ fontSize: 12, color: T.sub }}>{l}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: col, marginTop: 6 }}>{fmt(v)}</div>
                {main && <div style={{ marginTop: 4, display: "flex", gap: 6, alignItems: "center" }}>
                  <Pct value={cur.incomePct} T={T} />
                  {cur.incomePct != null && <span style={{ fontSize: 12, color: T.sub }}>{cur.compareLabel}</span>}
                </div>}
              </div>
            ))}
          </div>

          {/* 추이 */}
          <div style={cardStyle(T, isMobile ? 16 : 20)}>
            <div style={{ ...titleStyle(T), justifyContent: "space-between" }}>
              <span>📈 최근 6개월</span>
              <span style={{ display: "flex", gap: 12, fontSize: 12, fontWeight: 400, color: T.sub }}>
                <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: ACCENT, marginRight: 5 }} />파밍 수익</span>
                <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: DOWN, opacity: 0.75, marginRight: 5 }} />지출</span>
              </span>
            </div>
            <TrendChart cards={cards} T={T} isMobile={isMobile} />
          </div>

          {/* 월별 목록 */}
          <div style={{ ...cardStyle(T, isMobile ? 16 : 20), paddingBottom: 6 }}>
            <div style={{ ...titleStyle(T), marginBottom: 4 }}>🗓️ 월별 기록 <span style={{ fontSize: 12, fontWeight: 400, color: T.sub }}>· 눌러서 자세히</span></div>
            {cards.map((c, i) => (
              <MonthRow key={c.key} card={c} T={T} fmt={fmt} last={i === cards.length - 1}
                open={openKey === c.key} onToggle={() => setOpenKey(k => (k === c.key ? null : c.key))} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── 통계 탭: 월별 리포트 / 기존 기간 통계 전환 ──
export function StatsHub({ legacy, T, ...props }) {
  const [view, setView] = useState("monthly");
  return (
    <div>
      <div style={{ display: "inline-flex", gap: 4, background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 4, marginBottom: 14 }}>
        {[["monthly", "월별 리포트"], ["period", "기간별 통계"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)}
            style={{ padding: "8px 18px", borderRadius: 9, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
              background: view === k ? ACCENT : "transparent", color: view === k ? "#fff" : T.sub }}>
            {l}
          </button>
        ))}
      </div>
      {view === "monthly" ? <MonthlyReport T={T} {...props} /> : legacy}
    </div>
  );
}
